import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import AppBar from "../../common/AppBar.js";
import Loader from "../../common/Loader.js";
import Snackbar from "../../common/Snackbar.js";
import { SaveModal } from "../../common/Modals/index.js";
import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChangesBlocker.js";
import { getRevAllNodesWithStatus } from "../../redux/procedureRevisionSlice.js";
import { createSection, updateSection } from "../../redux/sectionSlice.js";
import { getAllVersionHistories } from "../../redux/versionHistorySlice.js";
import { getAuditTrailData } from "../../util/getAuditTrailData.js";
import { openPreview } from "../../redux/previewSlice.js";
import AddEditForm from "./AddEditForm.js";
import VersionHistoryPopup from "../../common/VersionHistoryPopup/VersionHistoryPopup";
import { useVersionHistoryDataFromNodes } from "../../common/VersionHistoryPopup/useVersionHistoryDataFromNodes.js";
import { buildPermissionContext } from "../../util/permissions";
import { setTaskReference } from "../Tasks/Uitlities";
import { pingTask } from "../../util/apiService";

const AddEditSection = ({
  mode = "add", // "add" or "edit"
  sectionId = null, // Required for edit mode
  onSuccess = null, // Custom success callback
  fromVersionHistory = false,
  versionHistoryData = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get URL parameters
  const { partnumber, revision, partId, revisionId, section_id_ref } =
    useParams();
  const [searchParams] = useSearchParams();
  const sectionIdFromQuery = searchParams.get("section_id");

  const { currentStatus,item_code_type } = useSelector((state) => state.procedureRevision);

  // Get effective section ID for edit mode
  const effectiveSectionId = mode === "edit" ? section_id_ref : null;

  // Form state - single state object for all form fields
  const [formData, setFormData] = useState({
    sectionName: "",
    shortDescription: "",
    isHide: false,
  });
  const [comment, setComment] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false); // Flag to disable blocker during navigation
  const [hasUnsavedChanges, setHasUnsavedChangesState] = useState(false);
  const hasUnsavedChangesRef = useRef(false);
  // Tracks whether the Lexical short-description editor has undo history —
  // i.e. the user has genuinely typed in it since it was initialised.
  const [isShortDescriptionDirty, setIsShortDescriptionDirty] = useState(false);

  const setHasUnsavedChanges = useCallback((value) => {
    hasUnsavedChangesRef.current = value;
    setHasUnsavedChangesState(value);
  }, []);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  // Section data (for edit mode)
  const [sectionData, setSectionData] = useState(null);
  const [lastSavedText, setLastSavedText] = useState("");
  const [initialFormData, setInitialFormData] = useState(null);

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState("");

  // All nodes data for calculating section count
  const [allNodesData, setAllNodesData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Get location state
  const { status, description, type } = location.state || {};

  // Build full URL with basename for navigation
  const buildFullUrl = (path) => {
    const pathname = window.location.pathname;
    const basename = pathname.split("/").filter(Boolean)[0];
    const base = basename ? `/${basename}` : "";
    return `${base}${path}`;
  };

  // Custom function to navigate without triggering unsaved changes alert
  const navigateWithoutAlert = (url) => {

    // Immediately set flags to disable the blocker
    setHasUnsavedChanges(false);
    setIsNavigating(true);

    try {
      // Create a hidden form and submit it to navigate
      // This method typically doesn't trigger beforeunload events
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = url;
      form.style.display = 'none';

      document.body.appendChild(form);
      form.submit();

      // Clean up
      setTimeout(() => {
        if (document.body.contains(form)) {
          document.body.removeChild(form);
        }
      }, 100);

    } catch (error) {
      console.error("❌ Form submission navigation failed:", error);

      // Fallback to window.location.replace
      try {
        window.location.replace(url);
      } catch (replaceError) {
        console.error("❌ window.location.replace also failed:", replaceError);
        // Final fallback
        window.location.href = url;
      }
    }
  };

  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  // Snackbar handlers
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Function to normalize HTML content for comparison
  const normalizeHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    
    // Remove editor-shell wrapper and normalize content
    let normalized = html;
    
    // Remove editor-shell div wrapper if present
    normalized = normalized.replace(/^<div class="editor-shell">(.*)<\/div>\s*$/s, '$1');
    
    // Remove empty paragraphs and normalize whitespace
    normalized = normalized.replace(/<p[^>]*>(\s|<br>|&nbsp;)*<\/p>/gi, '')
                              .replace(/&nbsp;/g, ' ')
                              .replace(/\s+/g, ' ')
                              .trim();
    
    // If after cleaning we have empty content, return empty string
    if (normalized === '' || normalized === '<p></p>' || normalized === '<p> </p>') {
      return '';
    }
    
    // Normalize quotes and convert to lowercase for comparison
    return normalized
      .replace(/"/g, '"')
      .replace(/'/g, "'")
      .toLowerCase();
  };

  // Function to check if form has actual changes compared to initial state.
  // Short description is intentionally excluded here — its dirty state is
  // tracked separately via the Lexical undo history (isShortDescriptionDirty).
  const checkForChanges = (currentFormData) => {
    if (!initialFormData) return false;
    
    const sectionNameChanged = currentFormData.sectionName !== initialFormData.sectionName;
    const isHideChanged = currentFormData.isHide !== initialFormData.isHide;
    
    return sectionNameChanged || isHideChanged;
  };

  // Stable callback forwarded to ShortDescriptionEditor via AddEditForm.
  // Fires with `true` when the user genuinely edits the editor (Lexical undo
  // stack becomes non-empty) and `false` when they undo all the way back.
  const handleShortDescriptionDirtyChange = useCallback(
    (dirty) => setIsShortDescriptionDirty(dirty),
    []
  );

  // Handle field change with proper change detection
  const handleFieldChange = (updatedFormData) => {
    const hasChanges = checkForChanges(updatedFormData);
    setHasUnsavedChanges(hasChanges);
  };

  // Fetch all revision nodes on mount
  useEffect(() => {
    const fetchNodes = async () => {
      setDataLoading(true);
      const result = await dispatch(
          getRevAllNodesWithStatus({
            partId: partId,
            revisionId: revisionId,
          })
        );
        setAllNodesData(result.payload.data);
        setDataLoading(false);
    };

    if (partId && revisionId) {
      fetchNodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partId, revisionId, dispatch]);

  // Get version histories from Redux
  const { versionHistories, loading: versionHistoryLoading } = useSelector(
    (state) => state.versionHistory,
  );

  // Ping API call every minute to keep lock alive
  useEffect(() => {
    if (!revisionId || !authUser) return;

    const pingInterval = setInterval(async () => {
      try {
        await pingTask({
          task_id: revisionId,
          user_id: authUser.id,
        });
      } catch (error) {
        console.error('Ping failed in AddEditSection:', error);
      }
    }, 60000); // 60 seconds = 1 minute

    return () => {
      clearInterval(pingInterval);
    };
  }, [revisionId, authUser]);

  const sectionVersionHistoryMatchIds = useMemo(
    () =>
      [
        effectiveSectionId,
        sectionData?.section_id_ref,
        sectionData?.detail?.section_tree_node_id,
      ].filter(Boolean),
    [
      effectiveSectionId,
      sectionData?.section_id_ref,
      sectionData?.detail?.section_tree_node_id,
    ],
  );

  /** Same ordering as ViewSection for sparse "Section N" audit lines in Node History. */
  const sectionDisplayOrdinalForNodeHistory = useMemo(() => {
    if (!effectiveSectionId || !Array.isArray(allNodesData)) return null;
    const compareSiblingOrders = (a, b) => {
      const intA = parseInt(a, 10);
      const intB = parseInt(b, 10);
      const safeA = Number.isFinite(intA) ? intA : 0;
      const safeB = Number.isFinite(intB) ? intB : 0;
      return safeA - safeB;
    };
    const getSectionSortOrderRaw = (s) =>
      s?.sibling_order ??
      s?.detail?.sibling_order ??
      s?.procedure_tree?.sibling_order;

    const sortedSections = [...allNodesData].sort((a, b) => {
      const orderA = getSectionSortOrderRaw(a);
      const orderB = getSectionSortOrderRaw(b);
      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

    const idx = sortedSections.findIndex(
      (section) => section.tree_node_id === effectiveSectionId,
    );
    return idx >= 0 ? idx + 1 : null;
  }, [allNodesData, effectiveSectionId]);

  const {
    mergedVersionHistories,
    allVersionHistories,
    versionHistoryTableLoading,
  } = useVersionHistoryDataFromNodes({
    isOpen: showVersionHistory && mode === "edit",
    revisionId,
    partId,
    allRevisionNodes: allNodesData,
    fallbackVersionId:
      sectionData?.detail?.version_id_ref ||
      sectionData?.version_id_ref ||
      null,
    nodesReady:
      mode === "edit" &&
      !dataLoading &&
      Array.isArray(allNodesData) &&
      allNodesData.length > 0,
  });

  // Auto-populate form data for edit mode from allNodesData
  useEffect(() => {
    if (mode !== "edit" || !effectiveSectionId || allNodesData?.length === 0)
      return;

    // Don't repopulate if we already have initial data (prevents continuous resets)
    if (initialFormData) return;

    // Find the section in allNodesData
    const currentSection = allNodesData?.find(
      (node) => node.tree_node_id === effectiveSectionId
    );

    if (currentSection) {
      setSectionData(currentSection);

      // Process short description to handle both plain text and HTML content
      let processedShortDescription = currentSection?.detail?.short_description || "";
      if (processedShortDescription && !processedShortDescription.includes('<div class="editor-shell">')) {
        // If it's plain text (no editor-shell wrapper), convert it to HTML format
        processedShortDescription = `<p>${processedShortDescription}</p>`;
      }

      // Populate form fields
      // Use the same clean initialization logic for the baseline to match the emitted value exactly
      const cleanBaselineHtml = (rawHtml) => {
        if (!rawHtml) return "";
        let cleaned = rawHtml.replace(/<div class="editor-shell">/g, "").replace(/<\/div>\s*$/, "");
        try {
          const parser = new DOMParser();
          const dom = parser.parseFromString(`<div>${cleaned}</div>`, "text/html");
          const container = dom.body.firstElementChild;
          if (container) {
            container.querySelectorAll("p").forEach(p => {
              p.classList.remove("PlaygroundEditorTheme__paragraph");
              p.removeAttribute("dir");
              if (p.getAttribute('class') === '') p.removeAttribute('class');
            });
            container.querySelectorAll("span[data-lexical-text]").forEach(span => {
              span.removeAttribute("data-lexical-text");
              if (!span.hasAttributes()) {
                const fragment = document.createDocumentFragment();
                while (span.firstChild) {
                  fragment.appendChild(span.firstChild);
                }
                span.parentNode.replaceChild(fragment, span);
              }
            });
            cleaned = container.innerHTML;
          }
        } catch (e) {}
        
        const isEmpty = cleaned.replace(/<p[^>]*>(\s|<br>|&nbsp;)*<\/p>/gi, '').trim() === '';
        return isEmpty ? "" : cleaned;
      };

      const newFormData = {
        sectionName: currentSection?.detail?.section_name || "",
        shortDescription: cleanBaselineHtml(processedShortDescription),
        isHide: currentSection?.detail?.is_hide_heading_and_desc || false,
      };
      
      setFormData(newFormData);
      setInitialFormData(newFormData); // Set initial state for change detection

      // Fetch version history to get the actual modifier user
      const versionIdRef =
        currentSection?.version_id_ref ||
        currentSection?.detail?.version_id_ref;
      if (versionIdRef) {
        dispatch(getAllVersionHistories({ versionId: versionIdRef }));
      }
    } else {
      showSnackbar("Section not found", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, effectiveSectionId, allNodesData]);

  // Set initial form data for add mode
  useEffect(() => {
    if (mode === "add" && !initialFormData) {
      const initialAddFormData = {
        sectionName: "",
        shortDescription: "",
        isHide: false,
      };
      setFormData(initialAddFormData);
      setInitialFormData(initialAddFormData);
    }
  }, [mode]); // Remove initialFormData from dependencies

  // Update last saved text when version history is loaded
  useEffect(() => {
    if (mode !== "edit" || !sectionData || versionHistories.length === 0)
      return;

    // Get the latest version history entry (most recent audit)
    const latestVersion = versionHistories[0];
    if (latestVersion) {
      const userName =
        `${latestVersion.first_name || ""} ${latestVersion.last_name || ""
          }`.trim() || "Unknown User";

      const timestamp = latestVersion.audit_date || sectionData?.updated_at;
      if (timestamp) {
        const { generateLastSavedText } = require("../../util/stepUtils");
        setLastSavedText(generateLastSavedText(timestamp, userName));
      }
    }
  }, [mode, sectionData, versionHistories]);

  // Intercept AppBar back navigation
  const handleAppBarBack = useCallback(() => {
    // Check for unsaved changes and show confirmation if needed
    if ((hasUnsavedChangesRef.current || isShortDescriptionDirty) && !isLoading) {
      const confirmed = window.confirm("You have unsaved changes. Are you sure you want to leave?");
      if (!confirmed) {
        return; // Don't navigate if user cancels
      }

      // User confirmed, disable blocker and navigate
      setHasUnsavedChanges(false);
      setIsShortDescriptionDirty(false);
      setIsNavigating(true);
    }

    // Force a page reload to ensure proper navigation
    setTimeout(() => {
      window.history.back();

      // Force page reload after navigation to ensure clean state
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }, 100);
  }, [isShortDescriptionDirty, isLoading]);

  // Use unsaved changes blocker for browser navigation (but not for our custom back button)
  // We handle the back button manually in handleAppBarBack
  useUnsavedChangesBlocker(
    (hasUnsavedChanges || isShortDescriptionDirty) && !isLoading && !isNavigating
  );

  // AppBar button handlers
  const handlePreviewClick = () => {
    // Validate mandatory fields before showing preview
    if (!formData.sectionName.trim()) {
      showSnackbar("Please fill the mandatory fields", "error");
      return;
    }

    // Calculate sibling_order for draft section
    let sibling_order = allNodesData?.length + 1;

    if (mode === "add" && sectionIdFromQuery) {
      // Find the section with the given section_id to add after
      const targetSection = allNodesData?.find(
        (node) => node.tree_node_id === sectionIdFromQuery
      );

      if (targetSection) {
        const targetSiblingOrder =
          targetSection.sibling_order ||
          targetSection.procedure_tree?.sibling_order;
        sibling_order = parseInt(targetSiblingOrder) + 1;
      }
    } else if (mode === "edit" && sectionData) {
      // Use existing sibling_order for edit mode
      sibling_order =
        sectionData.sibling_order || sectionData.procedure_tree?.sibling_order;
    }

    // Create draft section data to preview
    const draftSection = {
      tree_node_id:
        mode === "edit" ? effectiveSectionId : `draft_${Date.now()}`,
      sibling_order: sibling_order,
      detail: {
        section_name: formData.sectionName,
        short_description: formData.shortDescription,
        is_hide_heading_and_desc: formData.isHide,
      },
      steps: mode === "edit" && sectionData ? sectionData.steps || [] : [],
      created_at:
        mode === "edit" && sectionData
          ? sectionData.created_at
          : new Date().toISOString(),
      updated_at: new Date().toISOString(),
      isDraft: true, // Flag to identify this is unsaved
      mode: mode, // "add" or "edit"
    };

    // Open preview with draft section data
    dispatch(
      openPreview({
        partId,
        revisionId,
        draftSection,
      })
    );
  };

  const handleSaveClick = () => {
    if (!formData.sectionName.trim()) {
      showSnackbar("Section Name is mandatory.", "error");
      return;
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    // Don't clear comment here - preserve it when user closes modal without saving
  };

  const handleCreateSection = async () => {
    // Calculate sibling_order based on section_id query param
    let sibling_order = allNodesData?.length + 1;

    if (sectionIdFromQuery) {
      // Find the section with the given section_id
      const targetSection = allNodesData?.find(
        (node) => node.tree_node_id === sectionIdFromQuery
      );

      if (targetSection) {
        const targetSiblingOrder =
          targetSection.sibling_order ||
          targetSection.procedure_tree?.sibling_order;
        sibling_order = parseInt(targetSiblingOrder) + 1;
      }
    }

    const sectionDataPayload = {
      section_name: formData.sectionName,
      short_description: formData.shortDescription,
      part_required_id_ref: partId,
      revision_id_ref: revisionId,
      is_hide_heading_and_desc: formData.isHide,
      sibling_order,
    };

    const data = await dispatch(
      createSection({
        sectionData: sectionDataPayload,
        comment: comment,
        action_to_show: `Save ${pageTitle} for ${partnumber}, Rev ${revision}`,
        allNodesData,
        partnumber,
        revision,
        version_content: {
          partNumber: partnumber,
          revision,
          has_audit_trail_preview: true,
          pageTitle,
          breadcrumb_text,
          isHide: formData.isHide,
          sibling_order,
          part_id_ref: partId,
          procedure_rev_ref: revisionId,
          type: "section",
          status: currentStatus,
          item_code_type
        },
      })
    ).unwrap();

    if (data.success) {
      const { procedure_tree_ref } = data.results;
      showSnackbar("Section created successfully!", "success");
      setHasUnsavedChanges(false);
      setIsNavigating(true); // Disable blocker during navigation
      setComment(""); // Clear comment after successful save
      
      // Update initial form data after successful save
      setInitialFormData(formData);

      if (onSuccess) {
        onSuccess(data);
      } else {
        // Default navigation to the newly created section
        const navigationUrl = buildFullUrl(`/view-section/${partnumber}/${revision}/${partId}/${revisionId}/${procedure_tree_ref}`);

        setTimeout(() => {
          navigateWithoutAlert(navigationUrl);
        }, 200);
      }
    }
  };

  const handleUpdateSection = async () => {
    const payload = {
      section_name: formData.sectionName,
      short_description: formData.shortDescription,
      // is_hide_heading_and_desc: formData.isHide,
      part_required_id_ref: partId,
      revision_id_ref: revisionId,
      section_tree_node_id: sectionData?.detail?.section_tree_node_id,
      procedure_tree_id: sectionData?.detail?.procedure_tree_ref,
      sibling_order: sectionData?.sibling_order,
    };

    const audit_trail_data = await getAuditTrailData({
      action: "UPDATE_SECTION",
      comment: comment,
      parent_version:
        selectedVersionId ||
        sectionData?.version_id_ref ||
        sectionData?.detail?.version_id_ref,
      version_content: {
        ...payload,
        action_to_show: `Edit ${pageTitle} for ${partnumber}, Rev ${revision}`,
        isHide: formData.isHide,
        has_audit_trail_preview: true,
        breadcrumb_text,
        pageTitle: pageTitle,
        type: "section",
        part_id_ref: partId,
        procedure_rev_ref: revisionId,
        sibling_order: sectionData?.sibling_order,
        section_tree_node_id:
          sectionData?.section_tree_node_id || sectionData?.tree_node_id,
        partNumber: partnumber,
        revision,
        status: currentStatus,
        item_code_type:item_code_type,
      },
    });

    payload.audit_trail_data = audit_trail_data;

    // Use Redux thunk instead of direct axios call
    const response = await dispatch(
      updateSection({
        id: sectionData?.section_id_ref,
        data: payload,
        allNodesData,
        partnumber,
        revision,
        tree_node_id:sectionData?.tree_node_id,
      })
    ).unwrap();

    if (response) {
      showSnackbar("Section updated successfully", "success");
      setHasUnsavedChanges(false);
      setIsNavigating(true); // Disable blocker during navigation
      setComment(""); // Clear comment after successful save
      
      // Update initial form data after successful save
      setInitialFormData(formData);

      if (onSuccess) {
        onSuccess(response);
      } else {
        // Default navigation - go back to view section
        const navigationUrl = buildFullUrl(`/view-section/${partnumber}/${revision}/${partId}/${revisionId}/${effectiveSectionId}`);

        setTimeout(() => {
          navigateWithoutAlert(navigationUrl);
        }, 200);
      }
    }
  };

  const handleConfirmSave = async () => {
    if (!comment) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }
    setModalOpen(false);
    setIsLoading(true);

    try {
      if (mode === "add") {
        await handleCreateSection();
      } else {
        await handleUpdateSection();
      }
    } catch (error) {
      console.error("Error saving section:", error);
      showSnackbar("Error saving section. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate page title based on section count or section index for edit mode
  let pageTitle;

  if (mode === "edit" && effectiveSectionId && allNodesData?.length > 0) {
    // Sort sections by sibling_order to get the correct index
    const compareSiblingOrders = (a, b) => {
      const intA = parseInt(a, 10);
      const intB = parseInt(b, 10);
      return intA - intB;
    };

    const sortedSections = [...allNodesData].sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

    const sectionIndex = sortedSections.findIndex(
      (section) => section.tree_node_id === effectiveSectionId
    );

    pageTitle =
      sectionIndex >= 0
        ? `Section ${sectionIndex + 1}`
        : `Section ${allNodesData?.length + 1}`;
  } else {
    // For add mode
    const sectionCount = allNodesData?.length;
    pageTitle = `Section ${sectionCount + 1}`;
  }

  // Breadcrumb text
  const breadcrumb_text = `${partnumber} > Rev ${revision} > ${pageTitle}`;

  // Set task reference for edit mode
  useEffect(() => {
    if (mode === "edit" && sectionData) {
      setTaskReference(
        dispatch,
        `Section ${partnumber}`,
        sectionData?.detail?.section_tree_node_id,
        sectionData?.tree_node_id,
        sectionData?.ltree_path
      );
    }
  }, [mode, sectionData, dispatch, partnumber]);

  // Show loader while fetching data
  if (dataLoading) {
    return <Loader text="Loading section data..." />;
  }

  return (
    <div>
      {/* AppBar Component */}
      <AppBar
        subTitleAboveTitle={
          <>
            {localStorage.getItem("user_name")} is{" "}
            {mode === "add" ? "Creating" : "Editing"}
          </>
        }
        title={pageTitle}
        showTablesDropdown={true}
        showProfileButton={fromVersionHistory ? false : true}
        buttons={[
          { label: "Preview", onClick: handlePreviewClick },
          {
            label: "Save",
            onClick: canEdit ? handleSaveClick : undefined,
            disabled: !canEdit || isLoading,
            title: !canEdit
              ? "You do not have permission to save sections."
              : undefined,
            icon: isLoading ? <Loader /> : null,
          },
        ]}
        subheading={breadcrumb_text}
        showBlackTitle={true}
        onBack={handleAppBarBack}
        versionHistoryHandler={
          mode === "edit" ? () => setShowVersionHistory(true) : null
        }
        subheadingAfterTitle={mode === "edit" ? lastSavedText : null}
        fromVersionHistory={fromVersionHistory}
      />

      {/* Section Form */}
      <AddEditForm
        mode={mode}
        formData={formData}
        setFormData={setFormData}
        versionIdRef={sectionData?.version_id_ref || ""}
        onFieldChange={handleFieldChange}
        onShortDescriptionDirtyChange={handleShortDescriptionDirtyChange}
      />

      {/* Confirmation Modal */}
      <SaveModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleConfirmSave}
        title={`Save ${pageTitle}`}
        subtitle={`For ${partnumber} > Rev ${revision} > ${pageTitle}`}
        editorName={localStorage.getItem("user_name") || "Unknown User"}
        comment={comment}
        onCommentChange={(e) => setComment(e.target.value)}
        isLoading={isLoading}
        showLoader={true}
      />

      {/* Loader */}
      {isLoading && (
        <Loader text={`${mode === "add" ? "Creating" : "Saving"} section...`} />
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          procedurePartNumber={partnumber}
          procedureRevision={revision}
          versionHistories={mergedVersionHistories}
          mode={mode}
          breadcrumbText={breadcrumb_text}
          revisionTreeNodes={allNodesData}
          nodeId={
            sectionData?.tree_node_id ||
            effectiveSectionId ||
            section_id_ref ||
            ""
          }
          nodeIdField="section_tree_node_id"
          nodeHistoryMatchIds={
            Array.isArray(sectionVersionHistoryMatchIds) &&
            sectionVersionHistoryMatchIds.length > 0
              ? sectionVersionHistoryMatchIds
              : undefined
          }
          nodeHistorySectionOrdinal={
            sectionDisplayOrdinalForNodeHistory != null &&
            Number.isFinite(Number(sectionDisplayOrdinalForNodeHistory))
              ? Number(sectionDisplayOrdinalForNodeHistory)
              : undefined
          }
          procedureRevisionIdForNodeHistory={revisionId}
          restrictNodeHistoryToProcedureRevision={
            allVersionHistories.length === 0
          }
          emptyMessage={
            mode === "add"
              ? "No version history available for new sections"
              : "No version history available"
          }
          onViewDetails={(row) => {
            const aid = row.audit_trail_id || row.audit_trail_id_ref;
            if (!aid) return;
            if (row.version_id != null) {
              setSelectedVersionId(String(row.version_id));
            }
            setShowVersionHistory(false);
            navigate(`/audit-trail/preview/${aid}`);
          }}
          isRowViewActionDisabled={(row) =>
            !(row.audit_trail_id || row.audit_trail_id_ref)
          }
          exportCsvDisabled={versionHistoryTableLoading}
        />
      )}
    </div>
  );
};

export default AddEditSection;
